import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CreditCard, ExternalLink, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.tsx'
import { Button } from '../ui/button.tsx'
import { Alert, AlertDescription } from '../ui/alert.tsx'
import { getUserSubscriptions, createPortalSession } from '../../lib/stripe.ts'
import { formatDate } from '../../lib/utils.ts'
import type { SubscriptionDetails } from '../../lib/api/types.ts'

export function SubscriptionManagementSection() {
  const { t } = useTranslation()
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSubscription()
  }, [])

  const loadSubscription = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getUserSubscriptions()
      setSubscriptionData(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.subscription.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleManageClick = async () => {
    setIsRedirecting(true)
    setError(null)

    try {
      const { portal_url } = await createPortalSession()
      window.location.href = portal_url
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.subscription.manageError'))
      setIsRedirecting(false)
    }
  }

  const tier = subscriptionData?.entitlements?.subscription_tier || 'free'
  const subscription = subscriptionData?.subscription
  const hasSubscription = tier !== 'free' && subscription
  const cancelPending = subscription?.cancel_at_period_end

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-[#26247B]" />
            <CardTitle className="text-lg">{t('settings.subscription.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-[#6D6D6D]">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-[#26247B]" />
          <CardTitle className="text-lg">{t('settings.subscription.title')}</CardTitle>
        </div>
        <CardDescription>{t('settings.subscription.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasSubscription ? (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-[#F4F3F9] px-4 py-3">
                <span className="text-sm text-[#6D6D6D]">{t('settings.subscription.currentPlan')}</span>
                <span className="text-sm font-semibold text-[#363636] capitalize">{tier}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#F4F3F9] px-4 py-3">
                <span className="text-sm text-[#6D6D6D]">{t('settings.subscription.status')}</span>
                <span className="text-sm font-semibold text-[#363636]">
                  {cancelPending ? t('settings.subscription.endsOn') : t('settings.subscription.active')}
                </span>
              </div>
              {subscription.current_period_end && (
                <div className="flex items-center justify-between rounded-lg bg-[#F4F3F9] px-4 py-3">
                  <span className="text-sm text-[#6D6D6D]">
                    {cancelPending ? t('settings.subscription.endsOn') : t('settings.subscription.renewsOn')}
                  </span>
                  <span className="text-sm font-semibold text-[#363636]">
                    {formatDate(subscription.current_period_end)}
                  </span>
                </div>
              )}
            </div>

            {cancelPending && (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  {t('settings.subscription.cancelPending')}
                </AlertDescription>
              </Alert>
            )}

            {subscription.payment_provider === 'stripe' && subscription.stripe_account !== 'uk' && (
              <Button
                onClick={handleManageClick}
                disabled={isRedirecting}
                className="w-full sm:w-auto"
              >
                {isRedirecting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('settings.subscription.managingButton')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t('settings.subscription.manageButton')}
                    <ExternalLink className="h-4 w-4" />
                  </span>
                )}
              </Button>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between rounded-lg bg-[#F4F3F9] px-4 py-3">
            <span className="text-sm text-[#6D6D6D]">{t('settings.subscription.currentPlan')}</span>
            <span className="text-sm font-semibold text-[#363636]">{t('settings.subscription.freePlan')}</span>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
